import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, BookmarkCheck, NotebookPen, ExternalLink, AlertCircle, ListPlus, Plus } from "lucide-react";
import { isCreditsIncomplete } from "../data/shows";
import useStore from "../store/useStore";
import { useShowById } from "../hooks/useShows";
import { useMergedShow } from "../hooks/useMergedShow";
import Poster from "../components/Poster";
import LogCard from "../components/LogCard";
import { toast } from "sonner";
import { TOAST_MS } from "../lib/constants";
import { getShowLogs, getMyLists, addToList } from "../lib/services";
import { formatApiError } from "../lib/api";
import { lower } from "../lib/strings";

export default function Show() {
  const { id } = useParams();
  const navigate = useNavigate();
  const baseShow = useShowById(id);
  const show = useMergedShow(id) || baseShow;
  const { savedShows, toggleSave } = useStore();

  const [showListPicker, setShowListPicker] = useState(false);
  const [activity, setActivity] = useState([]);
  const [lists, setLists] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const [logsRes, listsRes] = await Promise.all([
        getShowLogs(id),
        getMyLists(),
      ]);
      setActivity(logsRes.items || []);
      setLists(listsRes.items || []);
    } catch (err) {
      console.warn("show page fetch failed:", err);
    } finally {
      setLoaded(true);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const onLogDeleted = (deletedId) => {
    setActivity((arr) => arr.filter((it) => it.log?.id !== deletedId));
  };

  if (!show) {
    return (
      <main className="min-h-screen bg-black text-white p-8 pb-28">
        <div className="font-display text-3xl">show not found.</div>
        <Link to="/toronto" className="mt-4 inline-block font-mono text-sm text-acid underline">
          back to toronto
        </Link>
      </main>
    );
  }

  const saved = savedShows.includes(show.id);
  const incomplete = isCreditsIncomplete(show);
  const updatedLocally = false;

  return (
    <main data-testid={`show-page-${show.id}`} className="min-h-screen bg-black text-white pb-28">
      <div className="px-5 pt-6 pb-3">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> back
        </button>
      </div>

      {/* hero */}
      <section className="px-5 pt-2 pb-6 border-b-2 border-white/15">
        <div className="flex gap-4 items-start">
          <div className="w-32 sm:w-40 shrink-0">
            <Poster show={show} size="hero" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
              {lower(show.theatre)}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl leading-[0.9] tracking-tight break-words">
              {lower(show.title)}
            </h1>
            <div className="mt-3 font-mono text-[11px] text-white/70 space-y-0.5">
              <div>{lower(show.dateLabel)}</div>
              <div className="text-white/50">{lower(show.venue)}</div>
            </div>
          </div>
        </div>

        {(show.director || show.playwright) && (
          <div className="mt-4 font-mono text-xs text-white/70 space-y-1">
            {show.director && (
              <div><span className="text-white/40">dir · </span>{lower(show.director)}</div>
            )}
            {show.playwright && (
              <div><span className="text-white/40">by · </span>{lower(show.playwright)}</div>
            )}
          </div>
        )}

        {show.description && (
          <p className="mt-4 font-mono text-xs text-white/70 leading-relaxed">
            {show.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {show.size && (
            <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border border-white/30 text-white/70">
              {show.size}
            </span>
          )}
          {(show.categories || []).slice(0, 3).map((c) => (
            <span key={c} className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border border-white/30 text-white/70">
              {c}
            </span>
          ))}
          {(incomplete || updatedLocally) && (
            <span data-testid="credits-incomplete-tag" className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${updatedLocally ? "border-acid text-acid" : "border-acid text-acid"}`}>
              {updatedLocally ? "credits updated locally" : "credits incomplete"}
            </span>
          )}
        </div>
      </section>

      {/* primary actions */}
      <section className="px-5 py-5 border-b-2 border-white/15 grid grid-cols-3 gap-2">
        <button
          data-testid="action-i-saw-this"
          onClick={() => navigate(`/log/${show.id}`)}
          className="font-mono text-[11px] lowercase border-2 border-acid text-acid px-2 py-3 hover:bg-acid hover:text-black transition-colors flex flex-col items-center gap-1"
        >
          <NotebookPen className="w-4 h-4" strokeWidth={2} />
          log
        </button>
        <button
          data-testid="action-save"
          onClick={() => {
            toggleSave(show.id);
            toast(saved ? "removed" : "saved", { duration: TOAST_MS.NORMAL });
          }}
          className={`font-mono text-[11px] lowercase border-2 px-2 py-3 transition-colors flex flex-col items-center gap-1 ${
            saved ? "border-acid bg-acid text-black" : "border-white hover:bg-white hover:text-black"
          }`}
        >
          {saved ? <BookmarkCheck className="w-4 h-4" strokeWidth={2} /> : <Bookmark className="w-4 h-4" strokeWidth={2} />}
          {saved ? "saved" : "save"}
        </button>
        {show.sourceUrl ? (
          <a
            data-testid="action-source"
            href={show.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-[11px] lowercase border-2 border-white/60 text-white/80 px-2 py-3 hover:bg-white hover:text-black transition-colors flex flex-col items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={2} />
            source
          </a>
        ) : (
          <div className="font-mono text-[10px] lowercase border-2 border-white/15 text-white/30 px-2 py-3 flex flex-col items-center gap-1">
            <ExternalLink className="w-4 h-4" strokeWidth={2} />
            no source
          </div>
        )}
      </section>

      {/* add to list */}
      <section className="px-5 py-4 border-b-2 border-white/15">
        <button
          data-testid="add-to-list-toggle"
          onClick={() => setShowListPicker((v) => !v)}
          className="font-mono text-[11px] lowercase border-2 border-white/40 text-white/80 px-3 py-2 hover:border-white transition-colors flex items-center gap-1.5"
        >
          <ListPlus className="w-3.5 h-3.5" strokeWidth={2} />
          add to list
        </button>

        {showListPicker && (
          <div data-testid="add-to-list-picker" className="mt-3 border border-white/15">
            {lists.length === 0 ? (
              <div className="px-3 py-3 font-mono text-xs text-white/50">
                no lists yet.
              </div>
            ) : (
              <ul>
                {lists.map((l) => (
                  <li key={l.id}>
                    <button
                      data-testid={`add-to-list-${l.id}`}
                      onClick={async () => {
                        try {
                          await addToList(l.id, show.id);
                          toast(`added to ${lower(l.title)}`, { duration: TOAST_MS.FAST });
                          setShowListPicker(false);
                        } catch (err) {
                          toast(formatApiError(err));
                        }
                      }}
                      className="w-full text-left flex items-center justify-between px-3 py-2.5 border-b border-white/10 hover:bg-acid/10 transition-colors"
                    >
                      <span className="font-display text-sm">{lower(l.title)}</span>
                      <span className="font-mono text-[10px] text-white/50">
                        {l.itemCount} shows
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Link
              to="/lists?create=1"
              data-testid="add-to-list-create-new"
              className="w-full text-left flex items-center gap-2 px-3 py-2.5 font-mono text-xs text-acid hover:bg-acid/10 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
              new list
            </Link>
          </div>
        )}
      </section>

      {/* activity */}
      <section data-testid="activity-section" className="px-5 py-6 border-b-2 border-white/15">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
          activity ({activity.length})
        </h2>
        {!loaded ? (
          <p className="font-mono text-xs text-white/40">loading...</p>
        ) : activity.length === 0 ? (
          <div className="border border-white/20 p-4 text-center">
            <p className="font-mono text-xs text-white/60">no logs yet.</p>
            <button
              onClick={() => navigate(`/log/${show.id}`)}
              data-testid="be-first-log"
              className="mt-3 font-mono text-[11px] lowercase border-2 border-acid text-acid px-3 py-2 hover:bg-acid hover:text-black transition-colors"
            >
              be the first to log this
            </button>
          </div>
        ) : (
          <ul className="space-y-5">
            {activity.map((item) => (
              <li key={item.log.id} className="border-b border-white/10 pb-5">
                <LogCard log={item.log} show={item.show || show} user={item.user} showUser={!!item.user} onDeleted={onLogDeleted} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* quick credits */}
      {(show.playwright || show.director || (show.cast && show.cast.length) || show.company || show.venue) && (
        <section className="px-5 py-6 border-b-2 border-white/15">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
            quick credits
          </h2>
          <dl className="font-mono text-xs text-white/80 space-y-2">
            {show.playwright && (
              <div className="flex gap-3"><dt className="text-white/40 w-24">playwright</dt><dd>{lower(show.playwright)}</dd></div>
            )}
            {show.director && (
              <div className="flex gap-3"><dt className="text-white/40 w-24">director</dt><dd>{lower(show.director)}</dd></div>
            )}
            {show.cast && show.cast.length > 0 && (
              <div className="flex gap-3"><dt className="text-white/40 w-24 shrink-0">cast</dt><dd className="leading-relaxed">{show.cast.slice(0, 5).map((c) => lower(c)).join(", ")}{show.cast.length > 5 ? `, +${show.cast.length - 5} more` : ""}</dd></div>
            )}
            {show.company && (
              <div className="flex gap-3"><dt className="text-white/40 w-24">company</dt><dd>{lower(show.company)}</dd></div>
            )}
            {show.venue && (
              <div className="flex gap-3"><dt className="text-white/40 w-24">venue</dt><dd>{lower(show.venue)}</dd></div>
            )}
          </dl>

          {(incomplete || updatedLocally) && (
            <div className={`mt-5 border-2 ${updatedLocally ? "border-acid/60" : "border-acid/60"} bg-acid/5 p-3`}>
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-acid shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex-1">
                  <div className="font-mono text-xs text-acid lowercase">
                    {updatedLocally ? "credits updated locally" : "credits incomplete"}
                  </div>
                  <p className="font-mono text-[11px] text-white/60 mt-1 leading-relaxed">
                    {updatedLocally
                      ? "in a real app, suggested credit changes would be reviewed before publishing."
                      : "some credits are missing."}
                  </p>
                  <Link
                    data-testid="suggest-credits-cta"
                    to={`/show/${show.id}/suggest-credits`}
                    className="mt-2 font-mono text-[11px] lowercase border-2 border-acid text-acid px-3 py-2 hover:bg-acid hover:text-black transition-colors inline-flex items-center gap-1.5"
                  >
                    suggest credits
                  </Link>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* creative team */}
      {show.creativeTeam && show.creativeTeam.length > 0 && (
        <section className="px-5 py-6 border-b-2 border-white/15">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
            creative team
          </h2>
          <ul className="font-mono text-xs space-y-1.5">
            {show.creativeTeam.map((c, i) => (
              <li key={`${c.name}-${i}`} className="flex gap-3 border-b border-white/10 pb-1.5">
                <span className="text-white/40 w-44 shrink-0">{lower(c.role)}</span>
                <span className="text-white/90">{lower(c.name)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {show.sourceUrl && (
        <section className="px-5 py-6">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
            source
          </h2>
          <a
            href={show.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-xs text-acid underline break-all"
          >
            {show.sourceUrl}
          </a>
        </section>
      )}
    </main>
  );
}
